import { Link } from "react-router-dom";
import { ShieldCheck, ArrowRight, Phone } from "lucide-react";
import Seo from "@/components/Seo";
import { PageHero, Reveal } from "@/components/Primitives";
import { SCREENING_STEPS, BUSINESS } from "@/data/site";

export default function Safety() {
  return (
    <>
      <Seo
        title="Safety & 11-Step Screening"
        description="Every Mom's Best Friend caregiver clears an 11-step screening: extensive application, screening interview, reference checks, criminal & sex offender registry checks, SSN/ID verification, motor vehicle record, education verification, CPR & First Aid training, agency training and a 7-year social media screening."
        breadcrumbs={[{ name: "Home", item: "/" }, { name: "Safety & Screening", item: "/safety-screening" }]}
      />
      <PageHero
        overline="Safety first, always"
        title="The 11-step screening behind every caregiver"
        subtitle="Trust isn't a tagline for us — it's a process. Here's exactly how we vet every single caregiver before they ever meet your family."
      />

      <section className="container-mbf py-20 md:py-28">
        <div className="grid lg:grid-cols-3 gap-8">
          <div className="lg:col-span-1">
            <div className="lg:sticky lg:top-28 bg-espresso text-parchment rounded-2xl p-8">
              <ShieldCheck className="w-10 h-10 text-gold mb-4" />
              <h2 className="font-serif text-2xl text-white mb-3">Zero shortcuts</h2>
              <p className="text-sm text-parchment/80 leading-relaxed mb-6">
                Fewer than 5% of applicants make it through our full screening. We only place caregivers we'd trust with our own children.
              </p>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between border-b border-parchment/15 pb-2"><span className="text-parchment/70">Screening steps</span><span className="font-semibold">11</span></div>
                <div className="flex justify-between border-b border-parchment/15 pb-2"><span className="text-parchment/70">CPR certified</span><span className="font-semibold">100%</span></div>
                <div className="flex justify-between"><span className="text-parchment/70">Experience required</span><span className="font-semibold">2+ years</span></div>
              </div>
            </div>
          </div>

          <div className="lg:col-span-2">
            <ol className="space-y-4">
              {SCREENING_STEPS.map((step, i) => (
                <Reveal key={step.n} delay={i * 0.03}>
                  <li className="card-warm p-6 flex gap-5 items-start" data-testid={`screening-step-${step.n}`}>
                    <div className="w-11 h-11 rounded-full bg-tinted text-espresso font-serif text-lg flex items-center justify-center shrink-0">{step.n}</div>
                    <div>
                      <h3 className="font-semibold text-lg text-espresso mb-1">{step.title}</h3>
                      <p className="text-sm text-stoneish leading-relaxed">{step.desc}</p>
                    </div>
                  </li>
                </Reveal>
              ))}
            </ol>
          </div>
        </div>

        <div className="mt-14 rounded-[2rem] bg-parchment-2 border border-[#F1E6D0] p-10 md:p-14 text-center">
          <h2 className="font-serif text-2xl md:text-3xl text-espresso">Care you can trust, from people you can trust</h2>
          <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
            <Link to="/book" className="btn-accent !px-8 !py-4" data-testid="safety-request-cta">Request a Sitter <ArrowRight className="w-4 h-4" /></Link>
            <a href={BUSINESS.phoneHref} className="btn-outline !px-8 !py-4" data-testid="safety-phone-link"><Phone className="w-4 h-4" /> {BUSINESS.phone}</a>
          </div>
        </div>
      </section>
    </>
  );
}
